import { AsyncPipe, DOCUMENT, Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Params, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { I18NextModule } from 'angular-i18next';
import { BehaviorSubject, debounceTime, from, mergeMap, Observable, of, tap } from 'rxjs';
import { BottomLinksComponent } from 'src/app/_shared/components/bottom-links/bottom-links.component';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { LazybgDirective } from 'src/app/common/image/lazybg.directive';
import { METADATA_SERVICE, MetadataService } from 'src/app/common/metadata/metadata.service';
import { RouteTranslatePipe } from 'src/app/_shared/pipes/route-translate.pipe';
import { SlugPipe } from 'src/app/_shared/pipes/slug.pipe';
import { FaqBody, SearchComparator } from 'src/types/graphql';
import { FooterComponent } from '../../_shared/components/footer/footer.component';
import { HeaderComponent } from '../../_shared/components/header/header.component';
import { FaqSearchComponent } from './faq-search/faq-search.component';
import { AllFaqsSearchGQL } from './faq.query.generated';
import { FaqService } from './faq.service';
import { BasePlusComponent } from '../../_shared/components/base/base-plus.component';

@Component({
  selector: 'sharry-faq',
  templateUrl: './faq.component.html',
  styleUrls: ['./faq.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    MatInputModule,
    I18NextModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    FaqSearchComponent,
    RouteTranslatePipe,
    IconComponent,
    SlugPipe,
    MatButtonModule,
    LazybgDirective,
    HeaderComponent,
    FooterComponent,
    BottomLinksComponent,
  ],
})
export class FaqComponent extends BasePlusComponent implements OnInit, OnDestroy {
  faqBodies$: Observable<FaqBody[]>;
  value$: Observable<string>;
  isSearch = false;
  value: string;
  params: Params;
  private faqBodiesSubject: BehaviorSubject<FaqBody[]>;
  private valueSubject: BehaviorSubject<string>;

  constructor(
    private cd: ChangeDetectorRef,
    private allFaqsSearchGQL: AllFaqsSearchGQL,
    private renderer: Renderer2,
    private router: Router,
    private route: ActivatedRoute,
    public faqService: FaqService,
    @Inject(METADATA_SERVICE) private metadataService: MetadataService,
    private location: Location,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
    this.faqBodiesSubject = new BehaviorSubject([]);
    this.faqBodies$ = this.faqBodiesSubject.asObservable();
    this.valueSubject = new BehaviorSubject('');
    this.value$ = this.valueSubject.asObservable();
  }

  ngOnInit(): void {
    this.metadataService.setMetadataPage('faq', this.location.path());
    this.renderer.addClass(this.document.querySelector('html'), 'shrink');
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        this.params = params;
        this.isSearch = params?.search === 'true';
        this.cd.detectChanges();
      }),
      this.value$
        .pipe(
          tap(value => (this.value = value)),
          debounceTime(300),
          mergeMap(value => {
            if (value?.length > 3) {
              return this.allFaqsSearchGQL.fetch({
                filter: {
                  or: [
                    {
                      query: {
                        field: 'title',
                        comparator: SearchComparator.ContWordStartsWith,
                        value,
                      },
                    },
                    {
                      query: {
                        field: 'body',
                        comparator: SearchComparator.ContWordStartsWith,
                        value,
                      },
                    },
                  ],
                },
              });
            }
            return of(null);
          }),
          tap(res => {
            this.faqBodiesSubject.next(res?.data?.faqSearch);
          }),
          mergeMap(() => {
            if ((this.value?.length > 0 || this.isSearch) && this.value?.length <= 3) {
              return from(
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: {
                    ...this.params,
                    search: undefined,
                  },
                }),
              );
            } else if (this.value?.length > 3) {
              return from(
                this.router.navigate([], {
                  relativeTo: this.route,
                  queryParams: {
                    ...this.params,
                    search: 'true',
                  },
                }),
              );
            }
            return of(null);
          }),
        )
        .subscribe({
          next: () => {
            this.cd.detectChanges();
          },
        }),
    );
  }

  onSearch(value: string) {
    this.valueSubject.next(value);
    this.cd.detectChanges();
  }

  onClearValue(search: HTMLInputElement) {
    search.value = '';
    this.onSearch(search.value);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.metadataService.clean();
    this.renderer.removeClass(this.document.querySelector('html'), 'shrink');
  }
}
